/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import * as Sentry from "@sentry/react";
import AutoCompleteDropdown from "../AutoCompleteDropdown/AutoCompleteDropdown";
import { SERVICES } from "../Services";
import { get } from "../ApiHandler/apihandler";

export const isEmptyObj = (obj) => Boolean(!obj || !Object.keys(obj).length);

export const getOptions = (options, key) =>
  options?.map((opt) => ({
    ...opt,
    value: opt[key],
  }));

export const ORGANIZATION_STRUCTURE_FIELDS = {
  AREA: "areaId",
  CLUSTER: "clusterId",
  ZONE: "zoneId",
  REGION: "regionId",
  CHAPTER: "chapterId",
  DISTRICT: "districtId",
  BLOCK: "blockId",
};

function OrganizationStructure({
  onUpdateValues,
  initialAreaId,
  hideDistrictBlock = false,
}) {
  const [area, setArea] = useState([]);
  const [cluster, setCluster] = useState([]);
  const [zone, setZone] = useState([]);
  const [region, setRegion] = useState([]);
  const [chapter, setChapter] = useState([]);
  const [district, setDistrict] = useState([]);
  const [block, setBlock] = useState([]);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({
    [ORGANIZATION_STRUCTURE_FIELDS.AREA]: null,
    [ORGANIZATION_STRUCTURE_FIELDS.CLUSTER]: null,
    [ORGANIZATION_STRUCTURE_FIELDS.ZONE]: null,
    [ORGANIZATION_STRUCTURE_FIELDS.REGION]: null,
    [ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]: null,
    [ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]: null,
    [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: null,
  });

  const containerSx = {
    width: "100%",
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: "8px",
    padding: "15px 10px",
    border: "1px solid #D7E3F4",
    borderRadius: "14px",
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    boxShadow: "none",

    "& .MuiFormControl-root": {
      width: "100%",
      minWidth: 0,
    },
    "& .MuiInputLabel-root": {
      fontSize: "13px",
      fontWeight: 600,
      color: "#52606D",
      top: "-4px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#265B83",
      top: "0px",
    },
    "& .MuiOutlinedInput-root": {
      height: "35px",
      minHeight: "35px",
      borderRadius: "10px",
      backgroundColor: "#FFFFFF",
      fontSize: "14px",
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      color: "#1F2937",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#8CA5BF",
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#265B83",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#265B83",
        borderWidth: "2px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "6px 10px !important",
      fontSize: "14px",
      fontWeight: 600,
    },
    "& .MuiAutocomplete-input": {
      minWidth: "0 !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      right: "8px",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
      color: "#265B83",
    },
  };

  const fieldSx = {
    flex: "1 1 0",
    width: 0,
    minWidth: 0,
    maxWidth: "100%",
  };

  const getArea = async () => {
    try {
      const url = `${SERVICES.AREA}`;
      const response = await get(url);
      if (response.ok) {
        const data = await response.json();
        setArea(data.data);
      } else {
        console.error("Failed to fetch data");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const getCluster = async (areaId) => {
    if (areaId) {
      try {
        const url = `${SERVICES.CLUSTER}?areaId=${areaId}`;
        const response = await get(url);
        if (response.ok) {
          const data = await response.json();
          setCluster(data.data);
        } else {
          console.error("Failed to fetch data");
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  const getZone = async (clusterId) => {
    if (clusterId) {
      try {
        const url = `${SERVICES.ZONE}?clusterId=${clusterId}`;
        const response = await get(url);
        if (response.ok) {
          const data = await response.json();
          setZone(data.data);
        } else {
          console.error("Failed to fetch data");
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  const getRegion = async (zoneId) => {
    if (zoneId) {
      try {
        const url = `${SERVICES.REGION}?zoneId=${zoneId}`;
        const response = await get(url);
        if (response.ok) {
          const data = await response.json();
          setRegion(data.data);
        } else {
          console.error("Failed to fetch data");
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  const getChapter = async (regionId) => {
    if (regionId) {
      try {
        const url = `${SERVICES.CHAPTER}?regionId=${regionId}`;
        const response = await get(url);
        if (response.ok) {
          const data = await response.json();
          setChapter(data.data);
        } else {
          console.error("Failed to fetch data");
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  const getDistrict = async (chapterId) => {
    if (chapterId) {
      try {
        const url = `${SERVICES.DISTRICT}?chapterId=${chapterId}`;
        const response = await get(url);
        if (response.ok) {
          const data = await response.json();
          setDistrict(data.data);
        } else {
          console.error("Failed to fetch data");
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  const getBlock = async (districtId) => {
    if (districtId) {
      try {
        const url = `${SERVICES.BLOCK}?districtId=${districtId}`;
        const response = await get(url);
        if (response.ok) {
          const data = await response.json();
          setBlock(data.data);
        } else {
          console.error("Failed to fetch data");
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  const updateValues = () => {
    onUpdateValues(values);
  };

  useEffect(() => {
    getArea();
    if (initialAreaId) {
      setValues((prevValues) => ({
        ...prevValues,
        [ORGANIZATION_STRUCTURE_FIELDS.AREA]: initialAreaId,
      }));
    }
  }, [initialAreaId]);

  useEffect(() => {
    getCluster();
    getZone();
    getRegion();
    getChapter();
    getDistrict();
    getBlock();
  }, []);

  useEffect(() => {
    updateValues();
  }, [values, onUpdateValues]);

  return (
    <Grid container sx={containerSx}>
      <Grid item sx={fieldSx}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.name}
          name={ORGANIZATION_STRUCTURE_FIELDS.AREA}
          options={getOptions(area, "structureId")}
          error={errors[ORGANIZATION_STRUCTURE_FIELDS.AREA]}
          value={values[ORGANIZATION_STRUCTURE_FIELDS.AREA]}
          label="Area"
          onChange={(selectedValue) => {
            const selectedAreaId = selectedValue
              ? selectedValue.structureId
              : null;

            setValues((prevValues) => ({
              ...prevValues,
              [ORGANIZATION_STRUCTURE_FIELDS.AREA]: selectedValue
                ? selectedValue.value
                : null,
            }));

            if (!selectedValue) {
              setValues({
                [ORGANIZATION_STRUCTURE_FIELDS.CLUSTER]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.ZONE]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.REGION]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: null,
              });
              setCluster([]);
              setZone([]);
              setRegion([]);
              setChapter([]);
              setDistrict([]);
              setBlock([]);
              return;
            }

            getCluster(selectedAreaId);
          }}
        />
      </Grid>

      <Grid item sx={fieldSx}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.name}
          name={ORGANIZATION_STRUCTURE_FIELDS.CLUSTER}
          options={getOptions(cluster, "structureId")}
          error={errors[ORGANIZATION_STRUCTURE_FIELDS.CLUSTER]}
          value={values[ORGANIZATION_STRUCTURE_FIELDS.CLUSTER]}
          label="Cluster"
          onChange={(selectedValue) => {
            const selectedClusterId = selectedValue
              ? selectedValue.structureId
              : null;

            setValues((prevValues) => ({
              ...prevValues,
              [ORGANIZATION_STRUCTURE_FIELDS.CLUSTER]: selectedValue
                ? selectedValue.value
                : null,
            }));

            if (!selectedValue) {
              setValues({
                [ORGANIZATION_STRUCTURE_FIELDS.ZONE]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.REGION]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: null,
              });
              setZone([]);
              setRegion([]);
              setChapter([]);
              setDistrict([]);
              setBlock([]);
              return;
            }

            getZone(selectedClusterId);
          }}
        />
      </Grid>

      <Grid item sx={fieldSx}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.name}
          name={ORGANIZATION_STRUCTURE_FIELDS.ZONE}
          options={getOptions(zone, "structureId")}
          error={errors[ORGANIZATION_STRUCTURE_FIELDS.ZONE]}
          value={values[ORGANIZATION_STRUCTURE_FIELDS.ZONE]}
          label="Zone"
          onChange={(selectedValue) => {
            const selectedZoneId = selectedValue
              ? selectedValue.structureId
              : null;

            setValues((prevValues) => ({
              ...prevValues,
              [ORGANIZATION_STRUCTURE_FIELDS.ZONE]: selectedValue
                ? selectedValue.value
                : null,
            }));

            if (!selectedValue) {
              setValues({
                [ORGANIZATION_STRUCTURE_FIELDS.REGION]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: null,
              });
              setRegion([]);
              setChapter([]);
              setDistrict([]);
              setBlock([]);
              return;
            }

            getRegion(selectedZoneId);
          }}
        />
      </Grid>

      <Grid item sx={fieldSx}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.name}
          name={ORGANIZATION_STRUCTURE_FIELDS.REGION}
          options={getOptions(region, "structureId")}
          error={errors[ORGANIZATION_STRUCTURE_FIELDS.REGION]}
          value={values[ORGANIZATION_STRUCTURE_FIELDS.REGION]}
          label="Region"
          onChange={(selectedValue) => {
            const selectedRegionId = selectedValue
              ? selectedValue.structureId
              : null;

            setValues((prevValues) => ({
              ...prevValues,
              [ORGANIZATION_STRUCTURE_FIELDS.REGION]: selectedValue
                ? selectedValue.value
                : null,
            }));

            if (!selectedValue) {
              setValues({
                [ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: null,
              });
              setChapter([]);
              setDistrict([]);
              setBlock([]);
              return;
            }

            getChapter(selectedRegionId);
          }}
        />
      </Grid>

      <Grid item sx={fieldSx}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.name}
          name={ORGANIZATION_STRUCTURE_FIELDS.CHAPTER}
          options={getOptions(chapter, "structureId")}
          error={errors[ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]}
          value={values[ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]}
          label="Chapter"
          onChange={(selectedValue) => {
            const selectedChapterId = selectedValue
              ? selectedValue.structureId
              : null;

            setValues((prevValues) => ({
              ...prevValues,
              [ORGANIZATION_STRUCTURE_FIELDS.CHAPTER]: selectedValue
                ? selectedValue.value
                : null,
            }));

            if (!selectedValue) {
              setValues({
                [ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]: null,
                [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: null,
              });
              setDistrict([]);
              setBlock([]);
              return;
            }

            getDistrict(selectedChapterId);
          }}
        />
      </Grid>

      {!hideDistrictBlock && (
        <Grid item sx={fieldSx}>
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.name}
            name={ORGANIZATION_STRUCTURE_FIELDS.DISTRICT}
            options={getOptions(district, "structureId")}
            error={errors[ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]}
            value={values[ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]}
            label="District"
            onChange={(selectedValue) => {
              const selectedDistrictId = selectedValue
                ? selectedValue.structureId
                : null;

              setValues((prevValues) => ({
                ...prevValues,
                [ORGANIZATION_STRUCTURE_FIELDS.DISTRICT]: selectedValue
                  ? selectedValue.value
                  : null,
              }));

              if (!selectedValue) {
                setValues({
                  [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: null,
                });
                setBlock([]);
                return;
              }

              getBlock(selectedDistrictId);
            }}
          />
        </Grid>
      )}

      {!hideDistrictBlock && (
        <Grid item sx={fieldSx}>
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.name}
            name={ORGANIZATION_STRUCTURE_FIELDS.BLOCK}
            options={getOptions(block, "structureId")}
            error={errors[ORGANIZATION_STRUCTURE_FIELDS.BLOCK]}
            value={values[ORGANIZATION_STRUCTURE_FIELDS.BLOCK]}
            label="Block"
            onChange={(selectedValue) => {
              setValues((prevValues) => ({
                ...prevValues,
                [ORGANIZATION_STRUCTURE_FIELDS.BLOCK]: selectedValue
                  ? selectedValue.value
                  : null,
              }));
            }}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default OrganizationStructure;
